import React, { useState } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import api from "../api";

function CharacterForm({ characterData, setCharacterData, onSubmit, isCreating, isSubmitting, onClose, loadingBarRef }) {
    const { t } = useTranslation();
    const [language, setLanguage] = useState('en');
    const [skillType, setSkillType] = useState('red');
    const [skillLang, setSkillLang] = useState('en');
    const [translating, setTranslating] = useState(false);

    const handleTranslate = async () => {
        const enTrans = characterData.translations?.en || {};
        const enSkills = (characterData.skills || []).filter((s) => s.lang === 'en');

        const hasTrans = ['ultimate_name', 'ultimate_description', 'specialized_weapons', 'description']
            .some((f) => (enTrans[f] || '').trim());
        if (!hasTrans && enSkills.length === 0) return;

        setTranslating(true);
        if (loadingBarRef?.current) loadingBarRef.current.continuousStart();
        try {
            const fields = {
                ultimate_name: enTrans.ultimate_name || '',
                ultimate_description: enTrans.ultimate_description || '',
                specialized_weapons: enTrans.specialized_weapons || '',
                description: enTrans.description || '',
            };
            enSkills.forEach((skill, i) => {
                fields[`skill_${i}_name`] = skill.name || '';
                fields[`skill_${i}_level1`] = skill.level1 || '';
                fields[`skill_${i}_level2`] = skill.level2 || '';
                fields[`skill_${i}_level3`] = skill.level3 || '';
            });

            const response = await api.post(`${process.env.REACT_APP_API_URL}/api/admin/translate`, { fields });
            const translations = response.data || {};

            setCharacterData((prev) => {
                const newTranslations = { ...prev.translations };
                ['ru', 'ua', 'ko', 'fr'].forEach((lang) => {
                    const tr = translations[lang] || {};
                    newTranslations[lang] = {
                        ultimate_name: tr.ultimate_name ?? '',
                        ultimate_description: tr.ultimate_description ?? '',
                        specialized_weapons: tr.specialized_weapons ?? '',
                        description: tr.description ?? '',
                    };
                });

                const newSkills = [...(prev.skills || [])];
                ['ru', 'ua', 'ko', 'fr'].forEach((lang) => {
                    const tr = translations[lang] || {};
                    enSkills.forEach((enSkill, i) => {
                        const translatedSkill = {
                            name: tr[`skill_${i}_name`] ?? '',
                            type: enSkill.type,
                            order: enSkill.order,
                            level1: tr[`skill_${i}_level1`] ?? '',
                            level2: tr[`skill_${i}_level2`] ?? '',
                            level3: tr[`skill_${i}_level3`] ?? '',
                            lang,
                        };
                        const existingIndex = newSkills.findIndex(
                            (s) => s.lang === lang && s.type === enSkill.type && String(s.order) === String(enSkill.order)
                        );
                        if (existingIndex >= 0) {
                            newSkills[existingIndex] = translatedSkill;
                        } else {
                            newSkills.push(translatedSkill);
                        }
                    });
                });

                return { ...prev, translations: newTranslations, skills: newSkills };
            });
        } catch (error) {
            Swal.fire({
                text: t('news.translateError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonText: t('posts.ok'),
                customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
            });
        } finally {
            setTranslating(false);
            if (loadingBarRef?.current) loadingBarRef.current.complete();
        }
    };

    const handleChange = (field, value, lang = null) => {
        if (lang) {
            setCharacterData(prev => ({
                ...prev,
                translations: {
                    ...prev.translations,
                    [lang]: {
                        ...prev.translations[lang] || {},
                        [field]: value
                    }
                }
            }));
        } else {
            setCharacterData(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleSkillChange = (index, field, value) => {
        const updatedSkills = [...characterData.skills];
        updatedSkills[index][field] = value;
        setCharacterData(prev => ({ ...prev, skills: updatedSkills }));
    };

    const addSkill = (type, lang) => {
        setCharacterData(prev => ({
            ...prev,
            skills: [...prev.skills, { name: '', type, order: 1, level1: '', level2: '', level3: '', lang }]
        }));
    };

    const removeLastSkill = (type, lang) => {
        const skillsForTypeAndLang = getSkillsForTypeAndLang(type, lang);
        if (skillsForTypeAndLang.length > 0) {
            const lastSkill = skillsForTypeAndLang[skillsForTypeAndLang.length - 1];
            setCharacterData(prev => ({
                ...prev,
                skills: prev.skills.filter(skill => skill !== lastSkill)
            }));
        }
    };

    const getSkillsForTypeAndLang = (type, lang) => {
        return characterData.skills.filter(skill => skill.type === type && skill.lang === lang);
    };

    return (
        <form onSubmit={onSubmit}>
            {onClose && (
                <button
                    type="button"
                    className="form-group mb-3 character-form-close-button"
                    onClick={onClose}
                >
                    {t('characters.close')}
                </button>
            )}

            <div className="form-section">
                <h4>{t('characters.basicInfo')}</h4>
                <div className="form-group">
                    {/*<label htmlFor="name">{t('characters.name')}</label>*/}
                    <input
                        type="text"
                        id="name"
                        placeholder={t('characters.name')}
                        className="form-control"
                        value={characterData.name || ''}
                        onChange={(e) => handleChange('name', e.target.value)}
                    />
                </div>
                <div className="form-group">
                    {/*<label htmlFor="logo_url">{t('characters.logoUrl')}</label>*/}
                    <input
                        type="text"
                        id="logo_url"
                        placeholder={t('characters.logoUrl')}
                        className="form-control"
                        value={characterData.logo_url || ''}
                        onChange={(e) => handleChange('logo_url', e.target.value)}
                    />
                </div>
                <div className="form-group">
                    {/*<label htmlFor="image_url">{t('characters.imageUrl')}</label>*/}
                    <input
                        type="text"
                        id="image_url"
                        placeholder={t('characters.imageUrl')}
                        className="form-control"
                        value={characterData.image_url || ''}
                        onChange={(e) => handleChange('image_url', e.target.value)}
                    />
                </div>
            </div>

            <div className="form-section">
                <h4>{t('characters.translations')}</h4>
                <Tabs activeKey={language} onSelect={(k) => setLanguage(k)} id="characters-language-tabs">
                    {['en', 'ru', 'ua', 'ko', 'fr'].map((lang) => (
                        <Tab eventKey={lang} title={t(`languages.${lang}`)} key={lang}>
                            <div className="form-group">
                                {/*<label htmlFor={`ultimate_name_${lang}`}>{t('characters.ultimateName')}</label>*/}
                                <input
                                    type="text"
                                    id={`ultimate_name_${lang}`}
                                    placeholder={t('characters.ultimateName')}
                                    className="form-control"
                                    value={characterData.translations[lang]?.ultimate_name || ''}
                                    onChange={(e) => handleChange('ultimate_name', e.target.value, lang)}
                                />
                            </div>
                            <div className="form-group">
                                {/*<label htmlFor={`ultimate_description_${lang}`}>{t('characters.ultimateDescription')}</label>*/}
                                <textarea
                                    id={`ultimate_description_${lang}`}
                                    placeholder={t('characters.ultimateDescription')}
                                    className="form-control"
                                    rows={3}
                                    value={characterData.translations[lang]?.ultimate_description || ''}
                                    onChange={(e) => handleChange('ultimate_description', e.target.value, lang)}
                                />
                            </div>
                            <div className="form-group">
                                {/*<label htmlFor={`specialized_weapons_${lang}`}>{t('characters.specializedWeapons')}</label>*/}
                                <input
                                    type="text"
                                    id={`specialized_weapons_${lang}`}
                                    placeholder={t('characters.specializedWeapons')}
                                    className="form-control"
                                    value={characterData.translations[lang]?.specialized_weapons || ''}
                                    onChange={(e) => handleChange('specialized_weapons', e.target.value, lang)}
                                />
                            </div>
                            <div className="form-group">
                                {/*<label htmlFor={`description_${lang}`}>{t('characters.description')}</label>*/}
                                <textarea
                                    id={`description_${lang}`}
                                    placeholder={t('characters.description')}
                                    className="form-control"
                                    rows={3}
                                    value={characterData.translations[lang]?.description || ''}
                                    onChange={(e) => handleChange('description', e.target.value, lang)}
                                />
                            </div>
                        </Tab>
                    ))}
                </Tabs>
            </div>

            <div className="form-section">
                <h4>{t('characters.skills')}</h4>
                <Tabs activeKey={skillType} onSelect={(k) => setSkillType(k)} id="skill-type-tabs" className="mb-3">
                    {['red', 'green', 'blue'].map((type) => (
                        <Tab eventKey={type} title={t(`characters.skillType_${type}`)} key={type}>
                            <Tabs activeKey={skillLang} onSelect={(k) => setSkillLang(k)} id={`skill-lang-tabs-${type}`} className="mb-3">
                                {['en', 'ru', 'ua', 'ko', 'fr'].map((lang) => (
                                    <Tab eventKey={lang} title={t(`languages.${lang}`)} key={lang}>
                                        {getSkillsForTypeAndLang(type, lang).length < 3 && (
                                            <button
                                                type="button"
                                                className="character-form-add-skill-button"
                                                onClick={() => addSkill(type, lang)}
                                            >
                                                {t('characters.addSkill')}
                                            </button>
                                        )}
                                        {getSkillsForTypeAndLang(type, lang).length > 0 && (
                                            <button
                                                type="button"
                                                className="character-form-remove-skill-button"
                                                onClick={() => removeLastSkill(type, lang)}
                                            >
                                                {t('characters.removeSkill')}
                                            </button>
                                        )}
                                        {getSkillsForTypeAndLang(type, lang).map((skill, index) => (
                                            <div key={index} className="skill-form p-3">
                                                <div className="form-group">
                                                    {/*<label htmlFor={`skill_name_${type}_${lang}_${index}`}>{t('characters.skillName')}</label>*/}
                                                    <input
                                                        type="text"
                                                        id={`skill_name_${type}_${lang}_${index}`}
                                                        placeholder={t('characters.skillName')}
                                                        className="form-control"
                                                        value={skill.name || ''}
                                                        onChange={(e) => handleSkillChange(characterData.skills.indexOf(skill), 'name', e.target.value)}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    {/*<label htmlFor={`skill_order_${type}_${lang}_${index}`}>{t('characters.skillOrder')}</label>*/}
                                                    <input
                                                        id={`skill_order_${type}_${lang}_${index}`}
                                                        placeholder={t('characters.skillOrder')}
                                                        className="form-control"
                                                        value={skill.order || ''}
                                                        onChange={(e) => handleSkillChange(characterData.skills.indexOf(skill), 'order', e.target.value)}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    {/*<label htmlFor={`skill_level1_${type}_${lang}_${index}`}>{t('characters.skillLevel1')}</label>*/}
                                                    <textarea
                                                        id={`skill_level1_${type}_${lang}_${index}`}
                                                        placeholder={t('characters.skillLevel1')}
                                                        className="form-control"
                                                        rows={2}
                                                        value={skill.level1 || ''}
                                                        onChange={(e) => handleSkillChange(characterData.skills.indexOf(skill), 'level1', e.target.value)}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    {/*<label htmlFor={`skill_level2_${type}_${lang}_${index}`}>{t('characters.skillLevel2')}</label>*/}
                                                    <textarea
                                                        id={`skill_level2_${type}_${lang}_${index}`}
                                                        placeholder={t('characters.skillLevel2')}
                                                        className="form-control"
                                                        rows={2}
                                                        value={skill.level2 || ''}
                                                        onChange={(e) => handleSkillChange(characterData.skills.indexOf(skill), 'level2', e.target.value)}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    {/*<label htmlFor={`skill_level3_${type}_${lang}_${index}`}>{t('characters.skillLevel3')}</label>*/}
                                                    <textarea
                                                        id={`skill_level3_${type}_${lang}_${index}`}
                                                        placeholder={t('characters.skillLevel3')}
                                                        className="form-control"
                                                        rows={2}
                                                        value={skill.level3 || ''}
                                                        onChange={(e) => handleSkillChange(characterData.skills.indexOf(skill), 'level3', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </Tab>
                                ))}
                            </Tabs>
                        </Tab>
                    ))}
                </Tabs>
            </div>

            <div className="form-buttons">
                <button
                    type="button"
                    className="preview-button"
                    onClick={handleTranslate}
                    disabled={translating}
                >
                    {translating ? t('news.translating') : t('news.translate')}
                </button>
                <button
                    type="submit"
                    className="character-form-submit-button"
                    disabled={isSubmitting}
                >
                    {isCreating ? t('characters.create') : t('characters.save')}
                </button>
            </div>
        </form>
    );
}

export default CharacterForm;